import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRightCircleIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { Button, Card, CardBody, Typography } from "@material-tailwind/react";
import { usePortalAuth } from "../context/PortalAuthContext";
import BookingSupportContactCard from "../components/BookingSupportContactCard";
import RevistaSubscribeForm from "../components/RevistaSubscribeForm";

export default function PortalCitasIndex() {
  const { user, isAuthenticated } = usePortalAuth();

  if (isAuthenticated && user) {
    if (user.role === "admin") {
      return <Navigate to="/admin" replace />;
    }
    if (user.role === "psychologist") {
      return <Navigate to="/psicologo" replace />;
    }
    return <Navigate to="/panel" replace />;
  }

  return (
    <div className="max-w-3xl mx-auto w-full min-w-0">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-white to-brand-lavender/10 border border-brand-brown/15 shadow-lg shadow-brand-brown/10 p-6 sm:p-10 mb-10">
        <div className="relative z-10">
          <Typography
            variant="h1"
            className="font-sans text-brand-dark text-3xl sm:text-4xl font-semibold leading-tight"
          >
            Tu bienestar, en un solo lugar
          </Typography>
          <p className="mt-4 text-brand-gray text-base sm:text-lg max-w-xl leading-relaxed">
            Agenda sesiones con psicólogos del programa EAP. Elige horario, confirma tu cita y
            recuérdala desde tu panel.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register" className="inline-flex">
              <Button
                size="lg"
                className="bg-brand-lavender text-white w-full sm:w-auto px-8 py-3 shadow-lg shadow-brand-lavender/30 normal-case text-base"
              >
                Crear cuenta
              </Button>
            </Link>
            <Link to="/login" className="inline-flex">
              <Button
                size="lg"
                variant="outlined"
                className="border-brand-brown/40 text-brand-dark w-full sm:w-auto px-8 py-3 normal-case text-base"
              >
                Ya tengo cuenta
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <BookingSupportContactCard className="mb-10" />

      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {[
          {
            icon: CalendarDaysIcon,
            title: "Agenda en minutos",
            text: "Elige psicólogo, día y horario con disponibilidad al instante.",
          },
          {
            icon: ShieldCheckIcon,
            title: "Programa PAE",
            text: "Servicio para colaboradores de empresas con contrato activo.",
          },
          {
            icon: CheckCircleIcon,
            title: "Gestiona tus citas",
            text: "Consulta y cancela desde «Mis citas» cuando lo necesites.",
          },
        ].map(({ icon: Icon, title, text }) => (
          <Card
            key={title}
            className="border border-brand-brown/15 shadow-sm bg-white/90"
          >
            <CardBody className="p-5">
              <Icon className="h-8 w-8 text-brand-lavender mb-3" aria-hidden />
              <Typography className="font-semibold text-brand-dark mb-1">
                {title}
              </Typography>
              <p className="text-sm text-brand-gray leading-relaxed">{text}</p>
            </CardBody>
          </Card>
        ))}
      </div>

      <Card className="border border-brand-brown/15 bg-white/80 mb-10">
        <CardBody className="p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <Typography className="font-semibold text-brand-dark text-lg">
              ¿Listo para empezar?
            </Typography>
            <p className="text-sm text-brand-gray mt-1">
              Regístrate con tu correo corporativo y completa tus datos.
            </p>
          </div>
          <Link to="/register" className="shrink-0 inline-flex items-center gap-2 text-brand-lavender font-semibold text-sm hover:underline">
            Ir al registro
            <ArrowRightCircleIcon className="h-5 w-5" aria-hidden />
          </Link>
        </CardBody>
      </Card>

      <section id="revista" className="rounded-2xl border border-brand-brown/15 bg-gradient-to-br from-white to-brand-lavender/10 p-6 sm:p-8">
        <Typography className="font-semibold text-brand-dark text-lg mb-1">
          Conexión Siete
        </Typography>
        <p className="text-sm text-brand-gray mb-5 max-w-lg leading-relaxed">
          Suscríbete y recibe el ejemplar vigente por correo. También disponible en{" "}
          <Link to="/revistas" className="text-brand-lavender font-medium hover:underline">
            /revistas
          </Link>
          .
        </p>
        <RevistaSubscribeForm source="landing" />
      </section>
    </div>
  );
}
